import { isEmpty, orderBy, remove } from 'lodash';
import { DateTime } from 'luxon';

import {
  BLOOD_PRESSURE,
  DIASTOLIC_BLOOD_PRESSURE,
  HEART_RATE,
  ObservationHistoryField,
  ObservationStatusType,
  ParsedActivityEvent,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
  SYSTOLIC_BLOOD_PRESSURE,
} from '../../constants';

const DATE_FORMAT = "LL/dd/yyyy 'at' h:mma";

export const formatObservationDate = (date: string): string =>
  DateTime.fromISO(date).toFormat(DATE_FORMAT).toLowerCase();

export const shouldShowClinicalProfilePositionTab = (
  tab: PatientVitalsTabs,
): boolean =>
  [
    PatientVitalsTabs.BLOOD_PRESSURE,
    PatientVitalsTabs.HEART_RATE,
    PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE,
  ].includes(tab);

// TODO remove
export const getNumColumns = (tab: PatientVitalsTabs): number => {
  return tab === PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE ? 31 : 13;
};

// TDOO remove
export const getValueColSpan = (tab: PatientVitalsTabs): number => {
  return tab === PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE ? 1 : 2;
};

export const MeasuredAtColSpan = 4;
export const PositionColSpan = 2;
export const DateSourceColSpan = 2;

export const GoalStatusColSpan = 2;
export const EditStatusColSpan = 3;

export const getValueColumnTitle = (tab: PatientVitalsTabs): string => {
  return PatientVitalsTabsCollection[tab].label;
};

const historyFieldMapper: Record<ObservationHistoryField, string> = {
  [ObservationHistoryField.CREATED]: 'Created',
  [ObservationHistoryField.DATETIME]: 'Date and hour',
  [ObservationHistoryField.INTERPRETATION]: 'Interpretation',
  // [ObservationHistoryField.NOTE]: 'Note',
  [ObservationHistoryField.POSITION]: 'Position',
  [ObservationHistoryField.SECONDARY_VALUE]: 'Secondary value',
  [ObservationHistoryField.STATUS]: 'Status',
  [ObservationHistoryField.VALUE]: 'Value',
  [ObservationHistoryField.NOTE]: 'Note',
};

export const isBloodPressureHeartRateTab = (tab: string): boolean =>
  tab === PatientVitalsTabs.HEART_RATE_BLOOD_PRESSURE;

const getFieldName = (
  fieldChanged: ObservationHistoryField,
  tab: PatientVitalsTabs,
): string => {
  if (
    isBloodPressureHeartRateTab(tab) &&
    [
      ObservationHistoryField.SECONDARY_VALUE,
      ObservationHistoryField.VALUE,
    ].includes(fieldChanged)
  )
    return fieldChanged === ObservationHistoryField.VALUE
      ? SYSTOLIC_BLOOD_PRESSURE
      : DIASTOLIC_BLOOD_PRESSURE;
  if (fieldChanged === ObservationHistoryField.VALUE)
    return PatientVitalsTabsCollection[tab].label;
  return historyFieldMapper[fieldChanged];
};

const formatHistoryValue = (
  value: string,
  fieldChanged: ObservationHistoryField,
  tab: PatientVitalsTabs,
): string => {
  if (!value) return '';
  if (fieldChanged === ObservationHistoryField.DATETIME)
    return formatObservationDate(value);
  if (
    fieldChanged === ObservationHistoryField.SECONDARY_VALUE ||
    fieldChanged === ObservationHistoryField.VALUE
  ) {
    const { unit = '' } = PatientVitalsTabsCollection[tab] ?? {};
    if (Array.isArray(unit)) {
      if (
        fieldChanged === ObservationHistoryField.VALUE ||
        fieldChanged === ObservationHistoryField.SECONDARY_VALUE
      ) {
        return `${value}${unit[0]}`;
      }
      return `${value}${unit[1]}`;
    }
    return `${value}${unit}`;
  }
  return value;
};

export const getHistoryChangeText = (
  { fieldChanged, newValue, previousValue, type }: ParsedActivityEvent,
  tab: PatientVitalsTabs,
): string => {
  if (fieldChanged === ObservationHistoryField.CREATED)
    return `Entry added (${type})`;
  if (
    fieldChanged === ObservationHistoryField.STATUS &&
    newValue === ObservationStatusType.VOID
  ) {
    return 'Voided';
  }
  const formattedNewValue = formatHistoryValue(newValue, fieldChanged, tab);
  const formattedPreviousValue = formatHistoryValue(
    previousValue,
    fieldChanged,
    tab,
  );
  const prevValueDisplay = formattedPreviousValue
    ? `from ${formattedPreviousValue} `
    : '';
  return `${getFieldName(
    fieldChanged,
    tab,
  )} edited ${prevValueDisplay}to ${formattedNewValue}`;
};

// Accepts the array and key
const groupByKey = (array, key) => {
  // Return the end result
  return array.reduce((result, currentValue) => {
    // If an array already present for key, push it to the array. Else create an array and push the object
    // eslint-disable-next-line no-param-reassign
    (result[currentValue[key]] = result[currentValue[key]] || []).push(
      currentValue,
    );
    // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
    return result;
  }, {}); // empty object is the initial value for result object
};

// Remove seconds and milliseconds
// This is for comparing everything in date, except for seconds and milliseconds
// "2023-04-02T12:47:44.338+0000" => "2023-04-02T12:47:00.000Z"
export const getProperDateString = (dateString: string): string => {
  if (dateString) {
    try {
      const dateValue = Date.parse(dateString);
      const date = new Date(dateValue);

      date.setSeconds(0);
      date.setMilliseconds(0);
      return date.toISOString();
    } catch (err) {
      console.error('Error parsing the date : ', err);
    }
  }
  return null;
};

export const combineHeartRateAndBloodPressureData = (
  observationData: PatientVitalsObservation[],
): [PatientVitalsObservation | null, PatientVitalsObservation | null][] => {
  // Group on type(blood pressure and heart rate)
  const groupedData = groupByKey(
    orderBy(observationData, 'observationDatetime', 'desc'),
    'type',
  );

  const hrData: PatientVitalsObservation[] = groupedData[HEART_RATE] ?? [];
  const bpData: PatientVitalsObservation[] = groupedData[BLOOD_PRESSURE] ?? [];

  if (bpData.length < 1) {
    return hrData.map((hrDataEntry) => {
      return [null, hrDataEntry];
    });
  }

  if (hrData.length < 1) {
    return bpData.map((bpDataEntry) => {
      return [null, bpDataEntry];
    });
  }

  const combinedData = [];

  bpData.forEach((bpDataEntry: PatientVitalsObservation) => {
    const hrMatchedEntries = remove(
      hrData,
      (f) =>
        f.observationDatetime === bpDataEntry.observationDatetime &&
        f.position === bpDataEntry.position &&
        f.interpretation === bpDataEntry.interpretation,
    );

    if (hrMatchedEntries.length > 0) {
      const hrDataEntry = hrMatchedEntries[0];
      combinedData.push([bpDataEntry, hrDataEntry]);

      if (hrMatchedEntries.length > 1) {
        combinedData.push(hrMatchedEntries.slice(1));
      }
    } else {
      combinedData.push([bpDataEntry, null]);
    }
  });

  // left over HR data
  if (hrData.length > 0) {
    combinedData.push();
    hrData.forEach((hrDataEntry) => combinedData.push([null, hrDataEntry]));
  }

  return combinedData;
};

const isPropValuesEqual = (subject, target, propNames) =>
  propNames.every((propName) => subject[propName] === target[propName]);

// Get unique items based on passed property names
// Duplicates will be made as single entry
export const getUniqueItemsByProperties = (
  items: ParsedActivityEvent[],
  propNames: string[],
): ParsedActivityEvent[] => {
  let filteredItems = [];
  if (!isEmpty(propNames) && !isEmpty(items)) {
    filteredItems =
      items?.filter(
        (item, index, array) =>
          index ===
          array.findIndex((foundItem) =>
            isPropValuesEqual(foundItem, item, propNames ?? []),
          ),
      ) ?? [];
  }
  return isEmpty(propNames) ? (items ?? []) : filteredItems;
};

// type Keys = keyof PatientVitalsObservation;
// type ReturnTypeData = ReturnType<typeof getObservationValueForProperty>;

export const getObservationValueForProperty = (
  observations: PatientVitalsObservation | PatientVitalsObservation[],
  property: string,
): string | number | PatientVitalsObservationSupplementalData => {
  if (Array.isArray(observations)) {
    return (observations[0] ?? observations[1])[property];
  }
  return observations[property];
};

export const getObservationBasedOnType = (
  observations: PatientVitalsObservation | PatientVitalsObservation[],
  observationType: string,
): PatientVitalsObservation => {
  if (Array.isArray(observations)) {
    const filteredObs: PatientVitalsObservation[] = observations.filter(
      (value) => value != null && value?.type === observationType,
    );

    if (filteredObs.length > 0) {
      return filteredObs[0];
    }

    return null;
  }
  return observations;
};
